import { useState } from 'react';
import {
  deleteContactById as deleteContactByIdRequest,
  getContactById as getContactByIdRequest,
  getContacts as getContactsRequest,
  saveContact as saveContactRequest,
  updateContact as updateContactRequest,
} from 'src/services/client';
import { toast } from 'react-toastify';
import { IContactPayloadForAPI } from 'src/utils/CommonHelpers/ContactAPIOperations/types';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';

interface UseContactAPIState {
  isSaving: boolean;
  error: string | null;
  lastResponse: any | null;

  contacts: IContact[];
  isLoadingContacts: boolean;
  contactsError: string | null;

  isDeleting: boolean;
  deleteError: string | null;
}

export interface ContactAPIResult {
  ok: boolean;
  response: any | null;
}

export interface FetchContactsResult {
  ok: boolean;
  contacts: IContact[];
}

export interface FetchContactByIdResult {
  ok: boolean;
  contact: IContact | null;
}

export interface DeleteContactResult {
  ok: boolean;
}

export interface UseContactAPIReturn {
  isSaving: boolean;
  error: string | null;
  lastResponse: any | null;
  saveContact: (payload: IContactPayloadForAPI) => Promise<ContactAPIResult>;
  updateContact: (payload: IContactPayloadForAPI) => Promise<ContactAPIResult>;
  clearError: () => void;
  clearResponse: () => void;

  contacts: IContact[];
  isLoadingContacts: boolean;
  contactsError: string | null;
  fetchContacts: () => Promise<FetchContactsResult>;
  fetchContactById: (contactId: string) => Promise<FetchContactByIdResult>;
  clearContactsError: () => void;

  isDeleting: boolean;
  deleteError: string | null;
  deleteContact: (contactId: string) => Promise<DeleteContactResult>;
  clearDeleteError: () => void;
}

/* Shared data-fetching hook for the Contact save/update API surface. Owns the
 * request lifecycle (isSaving / error / lastResponse) and the status-code →
 * toast + error-message branching that used to live inline in every
 * useContactFormSubmission* orchestrator. Page-specific orchestrators compose
 * this with their own payload builder + adminUser / role lookups. */
export const useContactAPI = (suiteId?: string): UseContactAPIReturn => {
  const [state, setState] = useState<UseContactAPIState>({
    isSaving: false,
    error: null,
    lastResponse: null,

    contacts: [],
    isLoadingContacts: false,
    contactsError: null,

    isDeleting: false,
    deleteError: null,
  });

  const submit = async (
    requestFn: (payload: IContactPayloadForAPI) => Promise<any>,
    payload: IContactPayloadForAPI,
    isUpdate: boolean
  ): Promise<ContactAPIResult> => {
    setState((prev: UseContactAPIState) => ({
      ...prev,
      isSaving: true,
      error: null,
      lastResponse: null,
    }));

    try {
      const response: any = await requestFn(payload);

      // legacy/workaround: apiReq is not shape-stable — it returns a raw Response
      // on 2xx/409/422/5xx, a pre-parsed { status, statusText, ok, message, data }
      // object on 400, and { isError: true } on auth/network failure. Normalize
      // before reading so a 400 (already-consumed body) doesn't crash on .json().
      if (response?.isError) {
        setState((prev: UseContactAPIState) => ({
          ...prev,
          isSaving: false,
          error: null,
          lastResponse: null,
        }));

        return { ok: false, response: null };
      }

      const isRawResponse: boolean = typeof response?.json === 'function';
      const responseData: any = isRawResponse
        ? await response.json()
        : response;
      const apiMessage: string | undefined = responseData?.message;

      if (response.status === 200 || response.status === 201) {
        const successMsg: string =
          apiMessage || (isUpdate ? 'Contact updated' : 'Contact added');

        toast.success(`ⓘ ${successMsg}`, {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseContactAPIState) => ({
          ...prev,
          isSaving: false,
          error: null,
          lastResponse: responseData,
        }));

        return { ok: true, response: responseData };
      }

      if (
        response.status === 400 ||
        response.status === 409 ||
        response.status === 422
      ) {
        const errorMsg: string =
          apiMessage ||
          (response.status === 409
            ? 'A conflict occurred: This contact may already exist.'
            : 'The request was invalid. Please check your input.');

        toast.error(`🚫 ${errorMsg}`, {
          autoClose: 12000,
          position: 'top-center',
        });

        setState((prev: UseContactAPIState) => ({
          ...prev,
          isSaving: false,
          error: errorMsg,
          lastResponse: null,
        }));

        return { ok: false, response: null };
      }

      if (response.status >= 500) {
        const serverError: string =
          'Internal Server Error. Please try again later.';

        toast.error(`⚠️ ${serverError}`, {
          autoClose: 12000,
          position: 'top-center',
        });

        setState((prev: UseContactAPIState) => ({
          ...prev,
          isSaving: false,
          error: serverError,
          lastResponse: null,
        }));

        return { ok: false, response: null };
      }

      throw new Error(`Unexpected response status: ${response.status}`);
    } catch (err: any) {
      console.error('useContactAPI submit failed:', err);

      const errorMessage: string =
        err?.message || 'Something went wrong, please try again.';

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });

      setState((prev: UseContactAPIState) => ({
        ...prev,
        isSaving: false,
        error: errorMessage,
        lastResponse: null,
      }));

      return { ok: false, response: null };
    }
  };

  /* Fetch the full contact list. Lazy — consumers call this explicitly when
   * they need the data (e.g. on dialogue open). Mirrors submit()'s status-code
   * → toast branching for consistency across the hook. */
  const fetchContacts = async (): Promise<FetchContactsResult> => {
    setState((prev: UseContactAPIState) => ({
      ...prev,
      isLoadingContacts: true,
      contactsError: null,
    }));

    try {
      const response: any = await getContactsRequest();

      if (response.ok || response.status === 200) {
        const data: IContact[] = await response.json();

        setState((prev: UseContactAPIState) => ({
          ...prev,
          contacts: data,
          isLoadingContacts: false,
          contactsError: null,
        }));

        return { ok: true, contacts: data };
      }

      const responseData: any = await response.json().catch(() => ({}));
      const apiMessage: string | undefined = responseData?.message;
      const fallback: string =
        response.status >= 500
          ? 'Internal Server Error. Please try again later.'
          : 'Failed to load contacts. Please try again.';
      const errorMessage: string = apiMessage || fallback;

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });

      setState((prev: UseContactAPIState) => ({
        ...prev,
        isLoadingContacts: false,
        contactsError: errorMessage,
      }));

      return { ok: false, contacts: [] };
    } catch (err: any) {
      console.error('useContactAPI fetchContacts failed:', err);

      const errorMessage: string =
        err?.message || 'Something went wrong while loading contacts.';

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });

      setState((prev: UseContactAPIState) => ({
        ...prev,
        isLoadingContacts: false,
        contactsError: errorMessage,
      }));

      return { ok: false, contacts: [] };
    }
  };

  const fetchContactById = async (
    contactId: string
  ): Promise<FetchContactByIdResult> => {
    try {
      const response: any = await getContactByIdRequest(contactId, suiteId);

      if (response.ok || response.status === 200) {
        const data: IContact = await response.json();

        return { ok: true, contact: data };
      }

      return { ok: false, contact: null };
    } catch (err: any) {
      console.error('useContactAPI fetchContactById failed:', err);

      return { ok: false, contact: null };
    }
  };

  const saveContact = (
    payload: IContactPayloadForAPI
  ): Promise<ContactAPIResult> => submit(saveContactRequest, payload, false);

  const updateContact = (
    payload: IContactPayloadForAPI
  ): Promise<ContactAPIResult> => submit(updateContactRequest, payload, true);

  const clearError = () => {
    setState((prev: UseContactAPIState) => ({ ...prev, error: null }));
  };

  const clearResponse = () => {
    setState((prev: UseContactAPIState) => ({ ...prev, lastResponse: null }));
  };

  const clearContactsError = () => {
    setState((prev: UseContactAPIState) => ({ ...prev, contactsError: null }));
  };

  const deleteContact = async (
    contactId: string
  ): Promise<DeleteContactResult> => {
    if (!contactId || typeof contactId !== 'string') {
      const invalidIdError: string =
        'Invalid contact ID provided for deletion.';

      toast.error(`🚫 ${invalidIdError}`, {
        autoClose: 12000,
        position: 'top-center',
      });

      setState((prev: UseContactAPIState) => ({
        ...prev,
        deleteError: invalidIdError,
      }));

      return { ok: false };
    }

    setState((prev: UseContactAPIState) => ({
      ...prev,
      isDeleting: true,
      deleteError: null,
    }));

    try {
      const response: any = await deleteContactByIdRequest(contactId);

      if (response.ok || response.status === 200) {
        toast.success(' ⓘ The user has been deleted.', {
          autoClose: 10000,
          position: 'top-center',
        });

        // Optimistic prune: remove the deleted contact from the local list so
        // consumers don't need to refetch to reflect the change.
        setState((prev: UseContactAPIState) => ({
          ...prev,
          contacts: prev.contacts.filter(
            (contact: IContact) => contact.id !== contactId
          ),
          isDeleting: false,
          deleteError: null,
        }));

        return { ok: true };
      }

      const responseData: any = await response.json().catch(() => ({}));
      const apiMessage: string | undefined = responseData?.message;
      const fallback: string =
        response.status >= 500
          ? 'Internal Server Error. Please try again later.'
          : `Failed to delete contact. Server responded with: ${response.status} ${response.statusText}`;
      const errorMessage: string = apiMessage || fallback;

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });

      setState((prev: UseContactAPIState) => ({
        ...prev,
        isDeleting: false,
        deleteError: errorMessage,
      }));

      return { ok: false };
    } catch (err: any) {
      console.error('useContactAPI deleteContact failed:', err);

      const errorMessage: string =
        err?.message || 'Something went wrong while deleting that contact.';

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });

      setState((prev: UseContactAPIState) => ({
        ...prev,
        isDeleting: false,
        deleteError: errorMessage,
      }));

      return { ok: false };
    }
  };

  const clearDeleteError = () => {
    setState((prev: UseContactAPIState) => ({ ...prev, deleteError: null }));
  };

  return {
    isSaving: state.isSaving,
    error: state.error,
    lastResponse: state.lastResponse,
    saveContact,
    updateContact,
    clearError,
    clearResponse,

    contacts: state.contacts,
    isLoadingContacts: state.isLoadingContacts,
    contactsError: state.contactsError,
    fetchContacts,
    fetchContactById,
    clearContactsError,

    isDeleting: state.isDeleting,
    deleteError: state.deleteError,
    deleteContact,
    clearDeleteError,
  };
};
